import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { FramerTooltip, usePagingStickyEmulationReports } from "@ryde/controls";
import { useReportsContext } from "@ryde/redux";
import { apiDataFetchingAtom, ReportRenderTypeEnum } from "@ryde/shared";

import { ReportTableHeadBulk } from "../bulk";
import { DeleteReportButton, SaveReportDialogButton } from "../dialogs";
import { ReportTableHeadDownload } from "../download";
import { FlexGroupingPanel } from "../flexGrouping";
import { getReportCategoryIcon } from "./report-icons";
import { ReportTableHeadChartButton } from "./report-table-head.chart-button";
import { ReportTableHeadSettings } from "./report-table-head.settings";
import { ReportTableHeadStyled } from "./reports.styled";

export const ReportTableHead: FC = () => {
  const { t } = useTranslation();
  const {
    reportRenderType,
    reportTitle,
    reportDescription,
    ownerCategory,
    isReportDeleteAvailableForReport,
    reportDto,
  } = useReportsContext();
  const isLoading = useRecoilValue(apiDataFetchingAtom);
  const mobileHeaderReportsRef = useRef<HTMLDivElement>(null);
  usePagingStickyEmulationReports({ pagingRef: mobileHeaderReportsRef });

  // Grid-only toolbar (chart/bulk/download/settings): hidden for RawData reports and when the
  // grid row limit was exceeded — in both cases rows are withheld and the page is download-only
  const isShownByCondition =
    reportRenderType !== ReportRenderTypeEnum.RawData &&
    !reportDto?.groupingParamsExpected &&
    !reportDto?.gridRowLimitExceeded;

  // Download-only reports (RawData, or a Regular report whose result exceeded the grid row limit) have no
  // grid, but users still choose which columns — and in what order — the CSV download contains. Show a
  // reduced toolbar: save the selection and open the column-settings drawer. Grid-only actions (chart,
  // bulk, client-side CSV) stay hidden.
  const isDownloadOnly =
    (reportRenderType === ReportRenderTypeEnum.RawData ||
      Boolean(reportDto?.gridRowLimitExceeded)) &&
    !reportDto?.groupingParamsExpected;

  return (
    <ReportTableHeadStyled ref={mobileHeaderReportsRef}>
      <div className="report_info_wrapper">
        <h2 className="report_title">
          {reportTitle}
          {ownerCategory && (
            <FramerTooltip content={t(`selectListLabel.${ownerCategory}`)}>
              <> {getReportCategoryIcon(ownerCategory)}</>
            </FramerTooltip>
          )}
        </h2>
        {reportDescription && (
          <p className="report_description"> {reportDescription}</p>
        )}
      </div>
      {!isLoading && <FlexGroupingPanel />}
      {isShownByCondition && (
        <div className="report_buttons">
          <ReportTableHeadChartButton />
          <ReportTableHeadBulk />
          <SaveReportDialogButton />
          {isReportDeleteAvailableForReport && <DeleteReportButton />}
          <ReportTableHeadDownload />
          <ReportTableHeadSettings />
        </div>
      )}
      {isDownloadOnly && (
        <div className="report_buttons">
          <SaveReportDialogButton />
          {isReportDeleteAvailableForReport && <DeleteReportButton />}
          <ReportTableHeadSettings />
        </div>
      )}
    </ReportTableHeadStyled>
  );
};
